/**
 * Ultra-lightweight, opt-in wall-time accumulator — exists ONLY to answer one benchmark
 * question: does the advisor's planning (macro-move enumeration + EV eval, `./advisor.ts`'s `rankMacroMoves`) or the referee's
 * per-command `structuredClone` (`packages/rules/src/encounter/referee/scratchExecutor.ts:78`)
 * dominate per-fight cost? `enabled` defaults to `false`, so every instrumented call site costs a
 * single boolean check when disabled — this is NOT a permanent profiler and carries no overhead
 * in normal test/CLI runs.
 *
 * Two buckets:
 *   - **`planning`**: wraps `./autoplay.ts`'s `rankMacroMoves` call.
 *   - **`referee`**: wraps `./directRefereeHandle.ts`'s `DirectRefereeHandle.submit` dispatch —
 *     the WHOLE per-command referee round-trip (the `refereeCommand`/`refereeMove`/`refereeIntent`
 *     call plus applying the returned plan through the real history), which is where
 *     `structuredClone` lives as a sub-cost, alongside the reducer's actual rule application
 *     (dice rolls, effect resolution, movement legality). This bucket therefore always contains
 *     the (separately-measured) clone cost as a subset — the benchmark script reports
 *     `referee - clone` as "referee apply beneath the clone" so the two never double-count.
 *
 * The sibling `structuredClone` bucket is measured separately, by the benchmark script
 * (`cli/scripts/bench-sweep.ts`) wrapping the GLOBAL `structuredClone` — no source change needed
 * in `packages/rules`, which never imports it (`scratchExecutor.ts` calls the bare global, so an
 * external `globalThis.structuredClone` override intercepts it cleanly).
 */

/** The shared counters — read directly after a timed run; call {@link resetPerfHooks} before each one so consecutive benchmark runs don't accumulate across each other. */
export const perfHooks = {
  enabled: false,
  planningNs: 0,
  planningCalls: 0,
  refereeNs: 0,
  refereeCalls: 0,
};

/** Zero every counter (leaves `enabled` untouched) — call before each timed run. */
export function resetPerfHooks(): void {
  perfHooks.planningNs = 0;
  perfHooks.planningCalls = 0;
  perfHooks.refereeNs = 0;
  perfHooks.refereeCalls = 0;
}

/** Time `fn` into the shared planning counters when `perfHooks.enabled`; otherwise just calls `fn()` (a single boolean check, no timer overhead). */
export function timePlanning<T>(fn: () => T): T {
  if (!perfHooks.enabled) return fn();
  const t0 = Bun.nanoseconds();
  const result = fn();
  perfHooks.planningNs += Bun.nanoseconds() - t0;
  perfHooks.planningCalls += 1;
  return result;
}

/** Time `fn` into the shared referee counters when `perfHooks.enabled`; otherwise just calls `fn()`. See this file's header — this bucket is the WHOLE per-command referee round-trip, which contains `structuredClone` as a sub-cost. */
export function timeReferee<T>(fn: () => T): T {
  if (!perfHooks.enabled) return fn();
  const t0 = Bun.nanoseconds();
  const result = fn();
  perfHooks.refereeNs += Bun.nanoseconds() - t0;
  perfHooks.refereeCalls += 1;
  return result;
}
